/**
 * Agent API Gateway
 * Extends the base APIGateway to provide specialized functionality for AI agent requests
 */

import { APIGateway, APIRequest, APIResponse, APIGatewayConfig } from './APIGateway';
import { AgentAuthenticationService } from './AgentAuthenticationService';
import { AgentRateLimitService } from './AgentRateLimitService';
import { agentRequestRouter } from './AgentRequestRouter';

export interface AgentAPIRequest extends APIRequest {
  agentId: string;
  endpoint: string;
  parameters: Record<string, any>;
  requestId: string;
  timestamp: Date;
}

export interface AgentAPIResponse extends APIResponse {
  success: boolean;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  metadata: {
    requestId: string;
    timestamp: Date;
    processingTime: number;
    rateLimit: {
      remaining: number;
      resetTime: Date;
    };
  };
}

export interface AgentAPIError {
  error: {
    code: string;
    message: string;
    details?: any;
    timestamp: Date;
    requestId: string;
    documentation?: string;
  };
  metadata: {
    rateLimit?: {
      remaining: number;
      resetTime: Date;
    };
  };
}

export class AgentAPIGateway {
  private static agentInstance: AgentAPIGateway;
  private baseGateway: APIGateway;
  private agentAuthService: AgentAuthenticationService;
  private agentRateLimitService: AgentRateLimitService;

  private constructor(config: APIGatewayConfig) {
    this.baseGateway = APIGateway.getInstance(config);
    this.agentAuthService = new AgentAuthenticationService();
    this.agentRateLimitService = new AgentRateLimitService();
  }

  static getInstance(config?: APIGatewayConfig): AgentAPIGateway {
    if (!AgentAPIGateway.agentInstance) {
      const defaultConfig: APIGatewayConfig = {
        defaultTimeout: 30000,
        maxRetryAttempts: 3,
        rateLimitConfig: {
          windowMs: 60000, // 1 minute
          maxRequests: 1000 // Higher limit for agents
        },
        enableLogging: true,
        enableMetrics: true
      };
      AgentAPIGateway.agentInstance = new AgentAPIGateway(config || defaultConfig);
    }
    return AgentAPIGateway.agentInstance;
  }

  /**
   * Process an agent API request with specialized handling
   */
  async processAgentRequest(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    const startTime = Date.now();
    
    try {
      // Authenticate agent
      const authResult = await this.agentAuthService.authenticateAgent(request.agentId, request.headers);
      if (!authResult.success) {
        throw this.createAgentError('AUTHENTICATION_FAILED', authResult.error || 'Authentication failed', 401);
      }

      // Check agent rate limits
      const rateLimitResult = await this.agentRateLimitService.checkAgentRateLimit(request.agentId);
      if (!rateLimitResult.allowed) {
        throw this.createAgentError('RATE_LIMIT_EXCEEDED', 
          `Rate limit exceeded. Try again in ${rateLimitResult.resetTime} seconds`, 
          429, 
          { rateLimitInfo: rateLimitResult }
        );
      }

      // Validate request format
      this.validateAgentRequest(request);

      // Route request to appropriate handler using AgentRequestRouter
      const agentResponse = await this.routeAgentRequest(request);

      return agentResponse;

    } catch (error) {
      return this.handleAgentError(error, request, startTime);
    }
  }

  /**
   * Route agent request using AgentRequestRouter
   */
  private async routeAgentRequest(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    // Initialize router with default routes if not already done
    if (agentRequestRouter.getRoutes().length === 0) {
      agentRequestRouter.registerDefaultRoutes();
    }

    // Route the request through the AgentRequestRouter
    return await agentRequestRouter.route(request);
  }

  /**
   * Validate agent request format
   */
  private validateAgentRequest(request: AgentAPIRequest): void {
    if (!request.agentId) {
      throw this.createAgentError('MISSING_AGENT_ID', 'Agent ID is required', 400);
    }

    if (!request.endpoint) {
      throw this.createAgentError('MISSING_ENDPOINT', 'Endpoint is required', 400);
    }

    if (!request.method) {
      throw this.createAgentError('MISSING_METHOD', 'HTTP method is required', 400);
    }

    if (!['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].includes(request.method)) {
      throw this.createAgentError('INVALID_METHOD', `Invalid HTTP method: ${request.method}`, 400);
    }

    if (!request.requestId) {
      throw this.createAgentError('MISSING_REQUEST_ID', 'Request ID is required', 400);
    }
  }

  /**
   * Transform response for agent consumption
   */
  private transformAgentResponse(
    baseResponse: APIResponse, 
    request: AgentAPIRequest, 
    startTime: number,
    rateLimitResult: any
  ): AgentAPIResponse {
    return {
      ...baseResponse,
      success: baseResponse.status >= 200 && baseResponse.status < 300,
      metadata: {
        requestId: request.requestId,
        timestamp: new Date(),
        processingTime: Date.now() - startTime,
        rateLimit: {
          remaining: rateLimitResult.remaining,
          resetTime: new Date(rateLimitResult.resetTime * 1000)
        }
      }
    };
  }

  /**
   * Handle agent-specific errors
   */
  private handleAgentError(error: any, request: AgentAPIRequest, startTime: number): AgentAPIResponse {
    let agentError: AgentAPIError;

    if (error.isAgentError) {
      agentError = error;
    } else {
      // Convert generic errors to agent errors
      agentError = this.createAgentError(
        'INTERNAL_ERROR',
        error.message || 'An internal error occurred',
        500,
        { originalError: error.message }
      );
    }

    return {
      status: agentError.error.code === 'RATE_LIMIT_EXCEEDED' ? 429 : 
              agentError.error.code === 'AUTHENTICATION_FAILED' ? 401 :
              agentError.error.code.includes('INVALID') ? 400 : 500,
      headers: {},
      data: null,
      duration: Date.now() - startTime,
      requestId: request.requestId,
      success: false,
      error: agentError.error,
      metadata: {
        requestId: request.requestId,
        timestamp: new Date(),
        processingTime: Date.now() - startTime,
        rateLimit: agentError.metadata.rateLimit || {
          remaining: 0,
          resetTime: new Date()
        }
      }
    };
  }

  /**
   * Create standardized agent error
   */
  private createAgentError(code: string, message: string, status: number, details?: any): any {
    const error = {
      error: {
        code,
        message,
        details,
        timestamp: new Date(),
        requestId: this.generateRequestId(),
        documentation: this.getDocumentationUrl(code)
      },
      metadata: {
        rateLimit: details?.rateLimitInfo
      },
      isAgentError: true
    };

    return error;
  }

  /**
   * Get documentation URL for error code
   */
  private getDocumentationUrl(code: string): string {
    const baseUrl = 'https://docs.example.com/api/errors';
    return `${baseUrl}#${code.toLowerCase()}`;
  }

  /**
   * Get base URL for internal API calls
   */
  private getBaseUrl(): string {
    // In a real implementation, this would come from configuration
    return process.env.API_BASE_URL || 'http://localhost:3000';
  }

  /**
   * Generate unique request ID
   */
  private generateRequestId(): string {
    return `agent_req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Agent-specific convenience methods
   */

  /**
   * Create campaign via agent API
   */
  async createCampaign(agentId: string, campaignData: any): Promise<AgentAPIResponse> {
    const request: AgentAPIRequest = {
      agentId,
      endpoint: 'campaigns/create',
      method: 'POST',
      url: '', // Will be set by routing
      headers: {},
      body: campaignData,
      parameters: {},
      requestId: this.generateRequestId(),
      timestamp: new Date()
    };

    return this.processAgentRequest(request);
  }

  /**
   * Get campaign status via agent API
   */
  async getCampaignStatus(agentId: string, campaignId: string): Promise<AgentAPIResponse> {
    const request: AgentAPIRequest = {
      agentId,
      endpoint: 'campaigns/status',
      method: 'GET',
      url: '', // Will be set by routing
      headers: {},
      parameters: { campaignId },
      requestId: this.generateRequestId(),
      timestamp: new Date()
    };

    return this.processAgentRequest(request);
  }

  /**
   * Send email via agent API
   */
  async sendEmail(agentId: string, emailData: any): Promise<AgentAPIResponse> {
    const request: AgentAPIRequest = {
      agentId,
      endpoint: 'emails/send',
      method: 'POST',
      url: '', // Will be set by routing
      headers: {},
      body: emailData,
      parameters: {},
      requestId: this.generateRequestId(),
      timestamp: new Date()
    };

    return this.processAgentRequest(request);
  }

  /**
   * Search leads via agent API
   */
  async searchLeads(agentId: string, searchParams: any): Promise<AgentAPIResponse> {
    const request: AgentAPIRequest = {
      agentId,
      endpoint: 'leads/search',
      method: 'GET',
      url: '', // Will be set by routing
      headers: {},
      parameters: searchParams,
      requestId: this.generateRequestId(),
      timestamp: new Date()
    };

    return this.processAgentRequest(request);
  }

  /**
   * Get analytics data via agent API
   */
  async getAnalytics(agentId: string, analyticsType: string, params: any = {}): Promise<AgentAPIResponse> {
    const request: AgentAPIRequest = {
      agentId,
      endpoint: `analytics/${analyticsType}`,
      method: 'GET',
      url: '', // Will be set by routing
      headers: {},
      parameters: params,
      requestId: this.generateRequestId(),
      timestamp: new Date()
    };

    return this.processAgentRequest(request);
  }
}

// Export singleton instance
export const agentAPIGateway = AgentAPIGateway.getInstance();